import { allowPageControlTabChange, isAutoSaving } from '../../../webmodule-common/other/save-workflow';
import { buttonsSaveCancel } from '../../../webmodule-common/other/ui/modal-confirmation';
import { ClientApi } from '../../api/client-api';
import { ClientContainer, ClientContainerManager as ClientContainerManager } from '../data/client-container-manager';
import { ClientDetailView } from './client-detail-view';
import { constructAsync } from '../../../webmodule-common/other/async-constructor';
import { customElement } from 'lit/decorators.js';
import { DataEntryOwner } from '../../../webmodule-common/other/ui/DataEntryOwner';
import { DataEntryPageControlView, ModalViewBase } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { EventBoolean, EventSnippet, EventTemplate, Snippet } from '../../../webmodule-common/other/ui/events';
import { getApiFactory } from '../../api/api-injector';
import { html, TemplateResult } from 'lit';
import {
  MenuIconOption,
  PageControl,
  PageControlOptions,
  PageManager
} from '../../../webmodule-common/other/ui/page-control';
import { tlang } from '../../../webmodule-common/other/language/lang';
import { validId } from '../../../webmodule-common/other/ui/string-helper-functions';

export interface ClientViewOptions {
  viewTitle: EventSnippet;
  clientContainerManager: ClientContainerManager;
}

@customElement('wm-clientdataentryview')
export class ClientDataEntryView extends DataEntryPageControlView {
  clientApi: ClientApi = getApiFactory().client();

  options: ClientViewOptions;
  protected clientContainerManager: ClientContainerManager;
  private readonly detailView: ClientDetailView;

  constructor(options: ClientViewOptions, owner: DataEntryOwner) {
    super(owner);

    this.options = options;
    this.clientContainerManager = options.clientContainerManager;

    this.detailView = this.clientDetailViewFactory();
  }

  get forceLocked(): EventBoolean | undefined {
    return this.options.clientContainerManager.forceLocked;
  }

  public get clientContainer(): ClientContainer {
    return this.clientContainerManager.container;
  }

  private get eventTitle(): EventSnippet {
    return this.options.viewTitle;
  }

  /**
   * inherited;
   * @returns
   */
  public async afterConstruction() {
    await this.clientContainerManager.needsClient();

    await constructAsync(this.detailView);
    //this will create the pagecontrol
    await super.afterConstruction();
    this.buildClientActionMenu();
  }

  /**
   * inherited;
   * @returns
   */
  public internalDataChanged(): boolean {
    return this.clientContainerManager.changed();
  }

  /**
   * inherited;
   * @returns
   */
  public isDataReadonly(): boolean {
    return this.clientContainerManager.isReadonly();
  }

  /**
   * inherited;
   * @returns
   */
  public async prepareForSave(): Promise<void> {
    if (this.isDataReadonly()) return;

    this.detailView.prepareForSave();
  }

  /**
   * inherited;
   * @returns
   */
  public getTitle(): Snippet {
    return html`${this.eventTitle()}`;
  }

  /**
   * inherited;
   * @returns
   */
  async allowPageSwitch(): Promise<boolean> {
    return await allowPageControlTabChange(this.getAutoSavePromptOptions());
  }

  public async saveAndClose(): Promise<boolean> {
    if (this.isDataReadonly()) return false;

    return await this.tryClose();
  }

  /**
   * inherited;
   * @returns
   */
  protected bodyTemplate(): EventTemplate {
    this.buildClientActionMenu();
    return super.bodyTemplate();
  }

  protected isNew(): boolean {
    return !validId(this.clientContainer.clientId);
  }

  /**
   * inherited;
   * @returns
   */
  protected createPageControl(): PageControl {
    // build static pages for each of the configured table settings
    const getInitialPageManagers = (): PageManager[] => {
      const pages: PageManager[] = [];
      pages.push(this.detailView?.createPageManager());
      return pages;
    };

    const options: PageControlOptions = {
      defaultTabIndex: 0,
      menuIcons: undefined,
      pageInitializer: () => getInitialPageManagers(),
      hideSingleTab: true
    };
    return new PageControl(options);
  }

  /**
   * inherited;
   * @returns
   */
  protected async internalSaveData(): Promise<boolean> {
    return await this.clientContainerManager.saveClient(isAutoSaving());
  }

  /**
   * inherited;
   * @returns
   */
  protected getValidationErrors() {
    const errors = this.detailView.getValidationErrors();
    return errors;
  }

  protected clientDetailViewFactory() {
    return new ClientDetailView(this, {
      clientContainerManager: this.clientContainerManager
    });
  }

  private buildClientActionMenu() {
    if (!this._pageControl) return;
    const menuIcons: MenuIconOption[] = [];

    const save = {
      event: async () => await this.saveAndClose(),
      caption: () => tlang`Save %%client%%`
    };

    if (!isAutoSaving()) menuIcons.push(save);

    this.pageControl.setMenuIcons(menuIcons);
  }
}

export class ClientView extends ModalViewBase {
  view: ClientDataEntryView | null = null;
  options: ClientViewOptions;

  constructor(options: ClientViewOptions) {
    super();
    this.options = options;
  }

  /**
   * inherited
   * @returns
   */
  protected get modalSize() {
    return 'modal-fullscreen';
  }

  /**
   * inherited
   * @returns
   */
  async canClose(): Promise<boolean> {
    return (await this.view?.canClose()) ?? true;
  }

  async afterConstruction(): Promise<void> {
    this.view = await constructAsync(this.createView());
  }

  /**
   * inherited
   * @returns
   */
  protected getTitle(): Snippet {
    return this.view?.getTitle() ?? '';
  }

  /**
   * inherited
   * @returns
   */
  protected footerTemplate(): TemplateResult | null {
    const buttons = buttonsSaveCancel();
    return this.createConfirmCancelButtons(buttons.ok, () => this.view?.saveAndClose(), buttons.cancel);
  }

  protected createView(): ClientDataEntryView {
    return new ClientDataEntryView(this.options, this);
  }

  protected bodyTemplate(): EventTemplate {
    return html`${this.view?.ui}`;
  }

  protected renderCloseButtonTemplate(): boolean {
    return false;
  }
}
